import React, { useEffect } from "react";
import { add, isAfter } from "date-fns";
import cn from "classnames";

import { MAX_DATES } from "config";
import useConfig from "hooks/useConfig";
import { getDefaultStation, countRailcards } from "lib/utils";
import { getSearchURL } from "lib/searchUrl";
import t from "lib/i18n";
import Button, { ButtonTypes } from "components/ui/Button";
import NewDatesSelector from "components/datetime/DatesSelector";
import Passengers from "components/Passengers";
import Promo from "components/Promo";
import MobileTicketRadio from "components/MobileTicketRadio";
import StationsSelector from "components/StationsSelector";
import useLayout from "hooks/useLayout";
import ViaAvoid from "components/ViaAvoid";
import ViaFind from "components/ViaFind";
import BookingFees from "components/BookingFees";
import { ReactComponent as DoubleArrowsIcon } from "static/images/double-arrows.svg";
import { ReactComponent as SingleArrowsIcon } from "static/images/single-arrow.svg";
import { ReactComponent as OpenReturnArrowsIcon } from "static/images/open-return-arrows.svg";
import "styles/pages/tickets.scss";
import TicketOptions from "components/tickets/TicketOptions";
import useGTM, { useGTMChange } from "hooks/useGTM";
import * as tracking from "lib/tracking";
import GatwickExpressInput from "components/GatwickExpressInput";
import { setLocalStorageItem } from "lib/localStorage";
import { TICKET_TYPES } from "consts";

const Tickets = ({ onGetValues, searchFormsController }) => {
  const {
    apiAccessToken,
    apiUrl,
    redirectUrl,
    searchCallBack,
    from,
    to,
    disableBackground,
    mode,
    utmCampaign,
    utmContent,
    utmMedium,
    utmSource,
    gatwickRoutes,
    enablePromoDiscount,
  } = useConfig();

  const {
    form,
    clearErrors,
    errors,
    handleSubmit,
    getValues,
    register,
    setValue,
    watch,
    localStorageKey
  } = searchFormsController.tickets;

  const { isMobile } = useLayout(true);

  const {
    adults,
    children,
    fromDateTime,
    railcards,
    ticketType,
    toDateTime,
    via,
    viaAvoid,
    viaAvoidType,
    promoCode,
  } = watch([
    "adults",
    "children",
    "fromDateTime",
    "railcards",
    "stationFrom",
    "stationTo",
    "ticketType",
    "toDateTime",
    "via",
    "viaAvoid",
    "viaAvoidType",
    "promoCode",
  ]);

  useEffect(() => {
    !form && setDefaultStations();

    register({ name: "adults" }, { required: true });
    register({ name: "children" }, { required: true });
    register(
      { name: "railcards" },
      {
        required: false,
        validate: {
          maximumRailcards: (value) => {
            return (
              countRailcards(value) <=
                getValues("adults") + getValues("children") ||
              "Too many railcards."
            );
          },
        },
      }
    );
    register(
      { name: "stationFrom" },
      {
        required: true,
        validate: {
          sameOrigin: ({ name }) => name !== getValues('stationTo')?.name
        }
      }
    );
    register(
      { name: "stationTo" },
      {
        required: true,
        validate: {
          sameOrigin: ({ name }) => name !== getValues('stationFrom')?.name
        }
      }
    );
    register({ name: "ticketType" }, { required: true });
    register(
      { name: "toDateTime" },
      {
        required: false,
        validate: {
          returnNotEarlier: (value) => {
            const isReturn = getValues("ticketType") === "return";
            // TODO different error message if the difference is less than 2 hours
            return (
              !isReturn ||
              isAfter(
                getValues("toDateTime").toDate(),
                getValues("fromDateTime").toDate()
              )
            );
          },
        },
      }
    );
    register({ name: "via" }, { required: false });
    register(
      { name: "viaAvoid" },
      {
        required: false,
        validate: {
          required: () => {
            if (getValues("via")) {
              return !!getValues("viaAvoid");
            }
            return true;
          },
        },
      }
    );
    register({ name: "viaAvoidType" }, { required: false });
    register({ name: "fromDateTime" }, { required: true });
    register({ name: "promoCode" }, { required: false });

    onGetValues(getValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchGTM = useGTM();
  const onSubmit = (data) => {
    const urlSearch = getSearchURL({
      data,
      redirectUrl,
      mode,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource,
    });
    dispatchGTM(tracking.search());

    setLocalStorageItem(localStorageKey, data);

    if (searchCallBack) {
      searchCallBack(urlSearch, data);
    } else {
      window.top.location.href = urlSearch;
    }
  };

  async function setDefaultStations() {
    if (from) {
      const station = await getDefaultStation(from, apiUrl, apiAccessToken);
      setValue("stationFrom", station, { shouldDirty: false });
    }

    if (to) {
      const station = await getDefaultStation(to, apiUrl, apiAccessToken);
      setValue("stationTo", station, { shouldDirty: false });
    }
  }

  const setFieldValue = (field) => (value) => {
    setValue(field, value, true);

    if (field === "ticketType") {
      clearErrors("toDateTime");

      if (value === "return" && !toDateTime) {
        setValue("toDateTime", fromDateTime.addHours(2).setLeavingAt());
      }
    }

    if (field === "stationTo" || field === "stationFrom") {
      clearErrors(field);
    }
  };

  const onChangeFromDateTime = (newValue) => {
    setValue("fromDateTime", fromDateTime?.produce(newValue), true);
  };

  const onChangeToDateTime = (newValue) => {
    setValue("toDateTime", toDateTime?.produce(newValue), true);
  };

  const isSingle = ticketType === "single";

  useGTMChange(via, tracking.trackViaAvoid);
  useGTMChange(viaAvoid, tracking.trackViaAvoidStation);
  useGTMChange(viaAvoidType, tracking.trackViaAvoidType);
  useGTMChange(ticketType, tracking.trackTicketType);
  useGTMChange(fromDateTime, tracking.trackFromDate);
  useGTMChange(toDateTime, tracking.trackToDate);
  useGTMChange(adults, tracking.trackAdults);
  useGTMChange(children, tracking.trackChildren);
  useGTMChange(railcards, tracking.trackRailcards);

  return (
    <div
      className={cn({
        "otrl-jp__widget__content": !disableBackground,
      })}
    >
      <div
        data-test-id='jp__tab--tickets'
        className={cn({
          "otrl-jp__widget__stations": !disableBackground,
        })}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {!isMobile && (
            <TicketOptions
              setFieldValue={setFieldValue}
              via={via}
              ticketType={ticketType}
            />
          )}
          <div className="otrl-jp__tickets__control-bar">
            <div className="otrl-jp__tickets__control-bar__stations">
              {!gatwickRoutes ? (
                <StationsSelector
                  clearErrors={clearErrors}
                  errors={errors}
                  getValues={getValues}
                  setValue={searchFormsController.onStationChange}
                  watch={watch}
                  ticketType={TICKET_TYPES.POINT}
                />
              ) : (
                <GatwickExpressInput
                  errors={errors}
                  setValue={setValue}
                  clearErrors={clearErrors}
                />
              )}
              {via && !isMobile && (
                <ViaAvoid
                  setValue={setValue}
                  viaAvoid={viaAvoid}
                  viaAvoidType={viaAvoidType}
                  errors={errors}
                />
              )}
            </div>

            <div className={cn("otrl-jp__tickets__horizontal-container", {
              "otrl-jp__tickets__horizontal-container--single": isSingle
            })}>
              {isMobile ? (
                <>
                  <ViaFind onChange={setFieldValue("via")} via={via} />
                    {via && (
                        <ViaAvoid
                            setValue={setValue}
                            viaAvoid={viaAvoid}
                            viaAvoidType={viaAvoidType}
                            errors={errors}
                        />
                    )}
                  <div className="otrl-jp__tickets__journey-type-and-dates">
                    <div className="otrl-jp__tickets__journey-type">
                      <MobileTicketRadio
                          Icon={SingleArrowsIcon}
                          label="Single"
                          ariaLabel="Single journey"
                          name="ticketType"
                          onChange={setFieldValue("ticketType")}
                          selected={ticketType}
                          value="single"
                      />
                      <MobileTicketRadio
                          icon={DoubleArrowsIcon}
                          name="ticketType"
                          label="Return"
                          ariaLabel="Return journey"
                          onChange={setFieldValue("ticketType")}
                          selected={ticketType}
                          value="return"
                      />
                      <MobileTicketRadio
                          Icon={OpenReturnArrowsIcon}
                          name="ticketType"
                          label="Open return"
                          ariaLabel="Open return journey"
                          onChange={setFieldValue("ticketType")}
                          selected={ticketType}
                          value="open"
                      />
                    </div>
                    <NewDatesSelector
                        errors={errors}
                        fromDateTime={fromDateTime}
                        onChangeFromDateTime={onChangeFromDateTime}
                        onChangeToDateTime={onChangeToDateTime}
                        ticketType={ticketType}
                        toDateTime={toDateTime}
                        maxDate={add(new Date(), MAX_DATES.SINGLES)}
                    />
                  </div>
                </>
              ) : (
                <NewDatesSelector
                  errors={errors}
                  fromDateTime={fromDateTime}
                  onChangeFromDateTime={onChangeFromDateTime}
                  onChangeToDateTime={onChangeToDateTime}
                  ticketType={ticketType}
                  toDateTime={toDateTime}
                  maxDate={add(new Date(), MAX_DATES.SINGLES)}
                />
              )}
              <Passengers
                adults={adults}
                children={children}
                railcards={railcards}
                onChange={setFieldValue}
                errorRailcards={errors["railcards"]}
              />

              {enablePromoDiscount && (
                <Promo onChange={setFieldValue} promoCode={promoCode} />
              )}
            </div>

            <Button
              className="otrl-jp__tickets__submit"
              text={t(isMobile ? "tickets.submit.long" : "tickets.submit")}
              type={ButtonTypes.SUBMIT}
            />
            <BookingFees />
          </div>
        </form>
      </div>
    </div>
  );
};

export default Tickets;
